import { useState } from 'react';
import Tesseract from 'tesseract.js';
import { extractBusinessCardInfo } from '../utils/ocrParser';
import { analyzeBusinessCardWithGemini, isGeminiAvailable } from '../services/geminiService';
import { normalizePhoneNumber } from '../utils/validators';
import { useLanguage } from '../context/LanguageContext';

export const useOCR = (language) => {
    const { t } = useLanguage();
    const [isProcessing, setIsProcessing] = useState(false);
    const [imagePreview, setImagePreview] = useState(null);
    const [message, setMessage] = useState({ type: '', text: '' });
    const [progress, setProgress] = useState(0);

    const processImage = async (file) => {
        if (!file) return null;

        if (!file.type.startsWith('image/')) {
            setMessage({ type: 'error', text: t.ocr?.invalidImage || 'Invalid image file' });
            return null;
        }

        // Set preview
        const reader = new FileReader();
        reader.onloadend = () => {
            setImagePreview(reader.result);
        };
        reader.readAsDataURL(file);

        setIsProcessing(true);
        setProgress(0);
        setMessage({ type: 'info', text: t.ocr?.processing || 'Processing...' });

        try {
            console.log('Starting Tesseract OCR...');

            // Use Tesseract.recognize directly (simpler approach)
            const tesseractLang = language === 'fr' ? 'fra' : 'eng';

            const result = await Tesseract.recognize(
                file,
                tesseractLang,
                {
                    logger: m => {
                        console.log('Tesseract progress:', m);
                        if (m.status === 'recognizing text' && m.progress) {
                            const progressPercent = Math.round(m.progress * 100);
                            setProgress(progressPercent);
                        }
                    }
                }
            );

            const text = result.data.text;
            console.log('Tesseract extracted text:', text);

            if (text && text.trim().length > 0) {
                let extractedData = null;

                // Try Gemini AI post-processing first (if available)
                if (isGeminiAvailable()) {
                    try {
                        console.log('Using Gemini AI for intelligent extraction...');
                        setMessage({ type: 'info', text: language === 'fr' ? 'Analyse IA en cours...' : 'AI analysis in progress...' });

                        extractedData = await analyzeBusinessCardWithGemini(text, language);
                        console.log('Gemini extracted data:', extractedData);

                        if (extractedData && Object.keys(extractedData).length > 0) {
                            console.log('Gemini extraction successful!');
                        } else {
                            console.log('Gemini returned empty data, falling back to basic parser');
                            extractedData = null;
                        }
                    } catch (geminiError) {
                        console.error('Gemini API error, falling back to basic parser:', geminiError);
                        extractedData = null;
                    }
                }

                // Fallback to basic parser if Gemini is not available or failed
                if (!extractedData) {
                    console.log('Using basic parser for extraction...');
                    extractedData = extractBusinessCardInfo(text);
                    console.log('Basic parser extracted data:', extractedData);
                }

                // Normalize phone number if present
                if (extractedData.phone) {
                    const normalized = normalizePhoneNumber(extractedData.phone);
                    if (normalized) {
                        extractedData.phone = normalized;
                    }
                }

                const fieldsFound = Object.keys(extractedData).length;
                if (fieldsFound > 0) {
                    const successMessage = isGeminiAvailable()
                        ? (language === 'fr' ? `✨ ${fieldsFound} champs extraits avec IA` : `✨ ${fieldsFound} fields extracted with AI`)
                        : `${t.ocr?.success || 'Analyzed!'} ${fieldsFound} ${t.ocr?.fieldsCompleted || 'field(s)'}`.trim();

                    setMessage({
                        type: 'success',
                        text: successMessage
                    });
                    setProgress(100);
                    return extractedData;
                } else {
                    setMessage({
                        type: 'warning',
                        text: t.ocr?.noInfo || 'No information extracted'
                    });
                    return null;
                }
            } else {
                setMessage({
                    type: 'warning',
                    text: t.ocr?.error || 'Analysis failed'
                });
                return null;
            }
        } catch (error) {
            console.error('Tesseract OCR Error:', error);
            console.error('Error details:', error.message, error.stack);
            setMessage({
                type: 'warning',
                text: t.ocr?.unavailable || 'OCR unavailable'
            });
            return null;
        } finally {
            setIsProcessing(false);
            setProgress(0);
        }
    };

    const clearImage = () => {
        setImagePreview(null);
        setMessage({ type: '', text: '' });
        setProgress(0);
    };

    return {
        isProcessing,
        imagePreview,
        message,
        progress,
        processImage,
        clearImage,
        setMessage
    };
};
